import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SModal from '../../../ui/molecules/SModal'
import Geocode from 'react-geocode'
import { observer } from 'mobx-react'
import { supplier, ui } from '@linqpal/common-frontend'
import { usePlacesWidget } from 'react-google-autocomplete'
import {
  GoogleMap,
  Marker,
  withGoogleMap,
  withScriptjs,
} from 'react-google-maps'
import Loading from '../../Loading'
import { GmapAddressInput } from './GmapAddressInput'
import { parseGmapAddress } from './parseGmapAddress'
import { IconClose } from '../../../assets/icons'
import { TouchableOpacity, View } from 'react-native'

const { Input } = supplier.ui

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY

export const AddressPicker = observer(
  ({ model, visible, onClose, testID = '' }) => {
    useEffect(() => {
      if (visible) {
        Geocode.setApiKey(GOOGLE_MAP_API_KEY)
        Geocode.setLanguage('en')
        Geocode.setRegion('us')
        Geocode.setLocationType('ROOFTOP')
      }
    }, [visible])

    const onPicked = useCallback(
      (address) => {
        if (address) {
          const setValue = model.setValue
          const { city, country, state, streetName, zipCode, streetNumber } =
            address
          setValue('zip', zipCode)
          setValue('city', city)
          setValue(
            'address',
            [streetNumber, streetName].filter(Boolean).join(' '),
          )
          setValue('state', state)
        }
      },
      [model],
    )

    const [markerPosition, setMarkerPosition] = useState({
      lat: CONFIG.lat,
      lng: CONFIG.lng,
    })

    const onMarkerDragEnd = useCallback(
      ({ latLng }) => {
        const lat = latLng.lat(),
          lng = latLng.lng()

        Geocode.fromLatLng(lat, lng).then((response) => {
          const address = parseGmapAddress(
            response.results[0].address_components,
          )
          onPicked(address)
          setMarkerPosition({ lat, lng })
        }, console.warn)
      },
      [onPicked],
    )

    const { lat, lng } = markerPosition

    const address = [model.address, model.city, model.state]
      .filter(Boolean)
      .join(', ')
    useEffect(() => {
      if (!address || !visible) return
      Geocode.fromAddress(address).then(
        (response) => {
          console.log('fromAddress', response)
          const { lat, lng } = response.results[0]?.geometry?.location || {}
          if (lat && lng) {
            setMarkerPosition({ lat, lng })
          }
        },
        (error) => console.log('fromAddress', { error }),
      )
    }, [address])

    const picker = useMemo(() => {
      if (visible) {
        return (
          <div style={{ width: '80%' }}>
            <Map
              googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}&libraries=geocode`}
              loadingElement={
                <div style={{ height: 100, width: 100 }}>
                  <Loading />
                </div>
              }
              containerElement={<div style={{ height: '400px', width: 800 }} />}
              mapElement={<div style={{ height: `100%`, width: '100%' }} />}
              lat={lat}
              lng={lng}
              onMarkerDragEnd={onMarkerDragEnd}
            />
          </div>
        )
      }
    }, [visible, onPicked, lat, lng, onMarkerDragEnd])

    return (
      <SModal
        visible={visible}
        animationIn="fadeIn"
        animationOut="fadeOut"
        onClose={onClose}
        contentStyle={{ padding: 0 }}
        containerStyle={{
          padding: 3,
          borderRadius: 5,
          backgroundColor: 'white',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 10,
          }}
          testID={testID}
        >
          <GmapAddressInput model={model} style={{ flex: 1 }} />
          <TouchableOpacity onPress={onClose}>
            <IconClose />
          </TouchableOpacity>
        </View>
        {picker}
      </SModal>
    )
  },
)

const addressAutocompleteParams = {
  apiKey: GOOGLE_MAP_API_KEY,
  options: {
    types: ['geocode', 'establishment'],
    componentRestrictions: { country: 'us' },
  },
}

// noinspection JSCheckFunctionSignatures
export const AddressAutocomplete = observer(function AddressAutocomplete({
  model,
  mandatory,
  highlight = false,
  testID = '',
}) {
  const setValue = model && model.setValue

  const onPlaceSelected = useCallback(
    (place) => {
      const { address_components } = place
      const address = parseGmapAddress(address_components)
      const {
        city,
        country,
        state,
        streetName = '',
        zipCode,
        streetNumber = '',
      } = address
      console.log({ city, state, streetName, streetNumber, zipCode })
      if (setValue) {
        setValue('zip', zipCode)
        setValue('city', city)
        setValue('address', `${streetNumber} ${streetName}`)
        setValue('state', state)
        setValue('lat', place.geometry.location.lat())
        setValue('lng', place.geometry.location.lng())
      }
    },
    [setValue],
  )

  // const cityAddress = `${model.address || ''} ${model.city || ''}`.trim()

  // const defaultAddress = [cityAddress, model.state]
  //   .filter(value => typeof value === 'string')
  //   .map(value => value.trim())
  //   .filter(value => value.length > 0)
  //   .join(', ')

  const AddressPicker = function AddressPicker({ highlight = false }) {
    // const [address, setAddress] = useState('')

    const { ref } = usePlacesWidget({
      ...addressAutocompleteParams,
      onPlaceSelected,
    })

    return useMemo(
      () => (
        <Input
          mandatory
          label="Street Address"
          ref={ref}
          model={model}
          name="address"
          validateInput={(value) =>
            !value.trim() && 'Please enter a valid value'
          }
          highlight={highlight}
          testID={testID}
        />
      ),
      [ref, highlight],
    )
  }

  return useMemo(
    () => <AddressPicker highlight={highlight} />,
    [AddressPicker, highlight],
  )
})

const CONFIG = { lat: 40.7063636, lng: -74.01561699999999 }

export const Map = withScriptjs(
  withGoogleMap(({ lat, lng, onMarkerDragEnd }) => {
    return (
      <GoogleMap
        defaultZoom={10}
        center={{ lat, lng }}
        onClick={onMarkerDragEnd}
      >
        <Marker draggable onDragEnd={onMarkerDragEnd} position={{ lat, lng }} />
      </GoogleMap>
    )
  }),
)
